import React, { createContext, useContext, useEffect, useState } from "react";

const LoaderContext = createContext({ isLoading: false });

let setActiveRequestsRef = null;

export function incrementActiveRequests() {
  setActiveRequestsRef?.((n) => n + 1);
}

export function decrementActiveRequests() {
  setActiveRequestsRef?.((n) => Math.max(0, n - 1));
}

// A reload/close mid-request abandons a call whose rotated token may never
// get stored, so the next call after reload can go out with a stale token.
// Block the keyboard-triggered reload shortcuts outright, and fall back to
// the browser's native "leave site?" confirmation for every other path
// (reload button, closing the tab, navigating away) — browsers don't allow
// pages to silently suppress those for security reasons.
function useBlockReloadWhileLoading(isLoading) {
  useEffect(() => {
    if (!isLoading) return;

    const blockReloadShortcut = (e) => {
      const isRefreshKey =
        e.key === "F5" || ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "r");
      if (isRefreshKey) e.preventDefault();
    };
    const confirmBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };

    document.addEventListener("keydown", blockReloadShortcut);
    window.addEventListener("beforeunload", confirmBeforeUnload);

    return () => {
      document.removeEventListener("keydown", blockReloadShortcut);
      window.removeEventListener("beforeunload", confirmBeforeUnload);
    };
  }, [isLoading]);
}

export function GlobalLoaderProvider({ children }) {
  const [activeRequests, setActiveRequests] = useState(0);
  setActiveRequestsRef = setActiveRequests;
  const isLoading = activeRequests > 0;

  useBlockReloadWhileLoading(isLoading);

  return (
    <LoaderContext.Provider value={{ isLoading }}>
      {children}
    </LoaderContext.Provider>
  );
}

export const useLoader = () => useContext(LoaderContext);
